import { useCallback, useEffect, useRef, useState } from "react";

/**
 * FASE 05 — Ajustes 25/08 (itens 3 e 4): o botão SALVAR só habilita quando há alteração.
 *
 * Antes, os 6 formulários usavam `disabled={saving}` — SALVAR ficava ativo desde a abertura,
 * mesmo numa consulta em que nada foi tocado. Não existia estado de "alterado" no módulo
 * (busca por isDirty/dirty/houveAlteracao em ProdutosN1 devolvia zero ocorrências).
 *
 * Guarda um retrato do estado na carga e compara com o atual. Voltar o campo ao valor
 * original desabilita o botão de novo — comparar por valor, não por "houve digitação".
 *
 * Em CADASTRO NOVO não há retrato a comparar: o hook devolve sempre `true`, para não
 * bloquear o primeiro salvamento.
 */
export function useFormularioAlterado<T>(
  valorAtual: T,
  opcoes?: {
    /** Cadastro novo: sem estado inicial para comparar, SALVAR fica sempre habilitado. */
    ehNovo?: boolean;
    /** Só passa a acompanhar quando os dados chegaram (evita capturar o retrato vazio). */
    pronto?: boolean;
  }
) {
  const { ehNovo = false, pronto = true } = opcoes ?? {};
  const retratoInicial = useRef<string | null>(null);
  const [alterado, setAlterado] = useState(false);

  // Normaliza para comparação estável: a ordem das chaves de um objeto não é garantida
  // entre renderizações, e `undefined` some no JSON.stringify padrão.
  const serializar = useCallback((valor: unknown): string => {
    return JSON.stringify(valor, (_chave, v) => {
      if (v === undefined) return null;
      if (v && typeof v === "object" && !Array.isArray(v)) {
        return Object.keys(v as Record<string, unknown>)
          .sort()
          .reduce<Record<string, unknown>>((acc, k) => {
            acc[k] = (v as Record<string, unknown>)[k];
            return acc;
          }, {});
      }
      return v;
    });
  }, []);

  // Enquanto está carregando, o retrato é descartado: toda recarga (inclusive a que acontece
  // depois de salvar mídias) rearma a comparação a partir do que veio do servidor. Sem isso,
  // o formulário voltaria "alterado" só por ter sido recarregado.
  useEffect(() => {
    if (!pronto) retratoInicial.current = null;
  }, [pronto]);

  useEffect(() => {
    if (!pronto) return;

    const atual = serializar(valorAtual);

    if (retratoInicial.current === null) {
      retratoInicial.current = atual;
      setAlterado(false);
      return;
    }

    setAlterado(atual !== retratoInicial.current);
  }, [valorAtual, pronto, serializar]);

  /** Rearma o retrato — usar depois de salvar, para SALVAR voltar a ficar desabilitado. */
  const marcarComoSalvo = useCallback(() => {
    retratoInicial.current = serializar(valorAtual);
    setAlterado(false);
  }, [valorAtual, serializar]);

  return { alterado: ehNovo ? true : alterado, marcarComoSalvo };
}
